import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Plus, Store, X, Pencil, Trash2, RefreshCw, Search,
  Users, Phone, Monitor, MapPin, FileText, Link2, Copy, DollarSign, UserPlus
} from "lucide-react";

// ── Schemas ──
const revendaSchema = z.object({ nome: z.string().min(1, "Nome obrigatorio") });
const lancamentoSchema = z.object({
  nome: z.string().min(1, "Nome obrigatorio"),
  endereco: z.string().optional().default(""),
  telefone: z.string().optional().default(""),
  quantidade_dispositivos: z.coerce.number().min(1).default(1),
  valor: z.coerce.number().min(0).default(0),
  codigo_launcher: z.string().optional().default(""),
  observacao: z.string().optional().default(""),
});

// ── RevendaCard ──
function RevendaCard({ revenda, onEdit, onDelete, onAddLancamento, onEditLancamento, onDeleteLancamento, onDeleteAllLancamentos, onCopyLink, refreshKey }) {
  const { authAxios } = useAuth();
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLancamentos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/rev-acomodados/${revenda.id}/lancamentos`);
      setLancamentos(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [authAxios, revenda.id]);

  useEffect(() => { fetchLancamentos(); }, [revenda.id, refreshKey, fetchLancamentos]);

  const handleDeleteAll = async () => { await onDeleteAllLancamentos(revenda); fetchLancamentos(); };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Store className="w-5 h-5 text-primary" /></div>
            <div>
              <CardTitle className="text-lg">{revenda.nome}</CardTitle>
              <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{revenda.total_clientes || 0} clientes</span>
                <span className="flex items-center gap-1"><Monitor className="w-3 h-3" />{revenda.total_dispositivos || 0} dispositivos</span>
                <span className="flex items-center gap-1 text-amber-400 font-semibold"><DollarSign className="w-3 h-3" />R$ {Number(revenda.soma_valor || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10" onClick={() => onCopyLink(revenda)} title="Copiar link"><Link2 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(revenda)}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(revenda.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium">Clientes Acomodados ({lancamentos.length})</p>
          <Button size="sm" onClick={() => onAddLancamento(revenda)} data-testid={`add-lancamento-${revenda.id}`}><Plus className="w-4 h-4 mr-1" /> Novo Cliente</Button>
        </div>
        {loading ? (
          <div className="text-center py-4"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : lancamentos.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhum cliente cadastrado</p>
        ) : (
          <div className="space-y-2">
            {lancamentos.map((l) => (
              <div key={l.id} className="p-3 rounded-lg border bg-muted/30 border-border/50">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-sm">{l.nome}</span>
                      <Badge variant="secondary" className="gap-1 text-xs"><Monitor className="w-3 h-3" />{l.quantidade_dispositivos}</Badge>
                      {l.valor > 0 && <Badge variant="outline" className="gap-1 text-xs text-emerald-400 border-emerald-400/30"><DollarSign className="w-3 h-3" />R$ {Number(l.valor).toFixed(2)}</Badge>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-muted-foreground mt-1">
                      {l.telefone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.telefone}</div>}
                      {l.endereco && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.endereco}</div>}
                      {l.codigo_launcher && <div className="flex items-center gap-1"><FileText className="w-3 h-3" /><span className="font-mono text-blue-400">{l.codigo_launcher}</span></div>}
                      {Array.isArray(l.codigos_launcher) && l.codigos_launcher.map((c, i) => (
                        <div key={`cl-${i}`} className="flex items-center gap-1"><FileText className="w-3 h-3" /><span className="font-mono text-blue-400">{c}</span></div>
                      ))}
                    </div>
                    {l.observacao && <p className="mt-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">{l.observacao}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEditLancamento(l, revenda)}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => onDeleteLancamento(l.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-border mt-3">
              <Button variant="destructive" size="sm" className="w-full" onClick={handleDeleteAll}><Trash2 className="w-4 h-4 mr-2" /> Excluir Todos os Clientes</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ──
export default function RevendaAcomodadoPage() {
  const { authAxios } = useAuth();
  const [revendas, setRevendas] = useState([]);
  const [stats, setStats] = useState({ total_revendas: 0, total_clientes: 0, total_dispositivos: 0, soma_total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterNome, setFilterNome] = useState("");

  // Dialog states
  const [revendaDialogOpen, setRevendaDialogOpen] = useState(false);
  const [editingRevenda, setEditingRevenda] = useState(null);
  const [lancDialogOpen, setLancDialogOpen] = useState(false);
  const [editingLanc, setEditingLanc] = useState(null);
  const [selectedRevenda, setSelectedRevenda] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAllTarget, setDeleteAllTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Funcionarios
  const [funcionarios, setFuncionarios] = useState([]);
  const [dConfigFunc, setDConfigFunc] = useState(false);
  const [novoFunc, setNovoFunc] = useState("");

  const revendaForm = useForm({ resolver: zodResolver(revendaSchema), defaultValues: { nome: "" } });
  const lancForm = useForm({ resolver: zodResolver(lancamentoSchema), defaultValues: { nome: "", endereco: "", telefone: "", quantidade_dispositivos: 1, valor: 0, codigo_launcher: "", observacao: "" } });

  const fetchRevendas = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterNome) params.append("nome", filterNome);
      const res = await authAxios.get(`/rev-acomodados?${params.toString()}`);
      setRevendas(res.data);
    } catch { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [authAxios, filterNome]);

  const fetchStats = async () => {
    try { const res = await authAxios.get("/rev-acomodados/stats/summary"); setStats(res.data); }
    catch { console.error("Erro stats"); }
  };

  const fetchFuncionarios = async () => {
    try { const res = await authAxios.get("/hub-empresa/funcionarios"); setFuncionarios(res.data); }
    catch { }
  };

  useEffect(() => { fetchRevendas(); fetchStats(); fetchFuncionarios(); }, [fetchRevendas]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await fetchRevendas(); await fetchStats(); setRefreshKey(k => k + 1); toast.success("Atualizado!"); }
    catch { toast.error("Erro"); }
    finally { setRefreshing(false); }
  };

  // Revenda CRUD
  const handleOpenRevendaDialog = (revenda = null) => {
    if (revenda) { setEditingRevenda(revenda); revendaForm.reset({ nome: revenda.nome }); }
    else { setEditingRevenda(null); revendaForm.reset({ nome: "" }); }
    setRevendaDialogOpen(true);
  };

  const handleSubmitRevenda = async (data) => {
    setSubmitting(true);
    try {
      if (editingRevenda) { await authAxios.put(`/rev-acomodados/${editingRevenda.id}`, data); toast.success("Atualizado!"); }
      else { await authAxios.post("/rev-acomodados", data); toast.success("Revenda criada!"); }
      setRevendaDialogOpen(false); fetchRevendas(); fetchStats();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteRevenda = async (id) => {
    if (!window.confirm("Excluir esta revenda e todos os clientes?")) return;
    try { await authAxios.delete(`/rev-acomodados/${id}`); toast.success("Removido!"); fetchRevendas(); fetchStats(); }
    catch { toast.error("Erro ao excluir"); }
  };

  // Lancamento CRUD
  const [codigosLauncher, setCodigosLauncher] = useState([]);

  const handleOpenLancDialog = (revenda, lanc = null) => {
    setSelectedRevenda(revenda);
    if (lanc) {
      setEditingLanc(lanc);
      lancForm.reset({ nome: lanc.nome, endereco: lanc.endereco || "", telefone: lanc.telefone || "", quantidade_dispositivos: lanc.quantidade_dispositivos || 1, valor: lanc.valor || 0, codigo_launcher: lanc.codigo_launcher || "", observacao: lanc.observacao || "" });
      setCodigosLauncher(Array.isArray(lanc.codigos_launcher) ? [...lanc.codigos_launcher] : []);
    } else {
      setEditingLanc(null);
      lancForm.reset({ nome: "", endereco: "", telefone: "", quantidade_dispositivos: 1, valor: 0, codigo_launcher: "", observacao: "" });
      setCodigosLauncher([]);
    }
    setLancDialogOpen(true);
  };

  const handleSubmitLanc = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data, codigos_launcher: codigosLauncher.filter(c => c && c.trim()) };
      if (editingLanc) { await authAxios.put(`/rev-acomodados/lancamentos/${editingLanc.id}`, payload); toast.success("Atualizado!"); }
      else { await authAxios.post("/rev-acomodados/lancamentos", { ...payload, revenda_id: selectedRevenda.id }); toast.success("Cliente cadastrado!"); }
      setLancDialogOpen(false); setRefreshKey(k => k + 1); fetchRevendas(); fetchStats();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteLanc = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    try { await authAxios.delete(`/rev-acomodados/lancamentos/${id}`); toast.success("Removido!"); setRefreshKey(k => k + 1); fetchRevendas(); fetchStats(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const handleOpenDeleteAll = (revenda) => { setDeleteAllTarget(revenda); setDeleteAllDialogOpen(true); };

  const handleCopyLink = async (revenda) => {
    let token = revenda.token_publico;
    if (!token) {
      try {
        const res = await authAxios.post(`/rev-acomodados/${revenda.id}/gerar-token`);
        token = res.data.token_publico;
        fetchRevendas();
      } catch { toast.error("Erro ao gerar link"); return; }
    }
    const url = `${window.location.origin}/rev-acomodado-publico/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
    catch { toast.info(url); }
  };
  const handleConfirmDeleteAll = async () => {
    if (!deleteAllTarget) return;
    try { await authAxios.delete(`/rev-acomodados/${deleteAllTarget.id}/lancamentos/all`); toast.success("Todos clientes removidos!"); setDeleteAllDialogOpen(false); setRefreshKey(k => k + 1); fetchRevendas(); fetchStats(); }
    catch { toast.error("Erro"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="revenda-acomodado-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Revenda Acomodado</h2>
          <p className="text-muted-foreground">Controle de clientes por revenda</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}><RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "..." : "Atualizar"}</Button>
          <Button onClick={() => handleOpenRevendaDialog()} data-testid="new-revenda-btn"><Plus className="w-4 h-4 mr-1" /> Nova Revenda</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-primary/10"><Store className="w-6 h-6 text-primary" /></div><div><p className="text-sm text-muted-foreground">Total Revendas</p><p className="text-xl font-bold">{stats.total_revendas}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-blue-500/10"><Users className="w-6 h-6 text-blue-500" /></div><div><p className="text-sm text-muted-foreground">Total Clientes</p><p className="text-xl font-bold">{stats.total_clientes}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-emerald-500/10"><Monitor className="w-6 h-6 text-emerald-500" /></div><div><p className="text-sm text-muted-foreground">Total Dispositivos</p><p className="text-xl font-bold">{stats.total_dispositivos}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-3 rounded-lg bg-amber-500/10"><DollarSign className="w-6 h-6 text-amber-500" /></div><div><p className="text-sm text-muted-foreground">Soma Total</p><p className="text-xl font-bold text-amber-400">R$ {Number(stats.soma_total).toFixed(2)}</p></div></div></CardContent></Card>
      </div>

      {/* Revendas Tabs */}
      {revendas.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground"><Store className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Nenhuma revenda acomodado cadastrada</p></CardContent></Card>
      ) : (() => {
        const filtered = filterNome ? revendas.filter(r => r.nome.toLowerCase().includes(filterNome.toLowerCase())) : revendas;
        return (
          <>
            <div className="flex items-center gap-1 max-w-xs">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Buscar revenda por nome..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} className="pl-10 h-9" data-testid="search-rev-aco" />
              </div>
              {filterNome && (
                <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setFilterNome("")}>
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            {filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">Nenhuma revenda encontrada</p>
            ) : (
              <Tabs defaultValue={filtered[0]?.id} key={filtered.map(r => r.id).join(",")} className="w-full">
                <div className="overflow-x-auto mb-4">
                  <TabsList className="inline-flex w-auto min-w-full h-auto flex-wrap gap-1 p-1" data-testid="rev-aco-tabs">
                    {filtered.map((r) => (
                      <TabsTrigger key={r.id} value={r.id} className="gap-1.5 whitespace-nowrap"><Store className="w-3.5 h-3.5" /> {r.nome}</TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                {filtered.map((revenda) => (
                  <TabsContent key={revenda.id} value={revenda.id}>
                    <RevendaCard
                      revenda={revenda}
                      onEdit={handleOpenRevendaDialog}
                      onDelete={handleDeleteRevenda}
                      onAddLancamento={(r) => handleOpenLancDialog(r)}
                      onEditLancamento={(l, r) => handleOpenLancDialog(r, l)}
                      onDeleteLancamento={handleDeleteLanc}
                      onDeleteAllLancamentos={handleOpenDeleteAll}
                      onCopyLink={handleCopyLink}
                      refreshKey={refreshKey}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </>
        );
      })()}

      {/* Dialog: Nova/Editar Revenda */}
      <Dialog open={revendaDialogOpen} onOpenChange={setRevendaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingRevenda ? "Editar" : "Nova"} Revenda</DialogTitle></DialogHeader>
          <form onSubmit={revendaForm.handleSubmit(handleSubmitRevenda)} className="space-y-4">
            <div className="space-y-2"><Label>Nome *</Label><Input {...revendaForm.register("nome")} placeholder="Nome da revenda" data-testid="input-revenda-nome" />{revendaForm.formState.errors.nome && <p className="text-xs text-destructive">{revendaForm.formState.errors.nome.message}</p>}</div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRevendaDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Novo/Editar Cliente (Lancamento) */}
      <Dialog open={lancDialogOpen} onOpenChange={setLancDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingLanc ? "Editar" : "Novo"} Cliente</DialogTitle>
            {selectedRevenda && <p className="text-sm text-muted-foreground">{selectedRevenda.nome}</p>}
          </DialogHeader>
          <form onSubmit={lancForm.handleSubmit(handleSubmitLanc)} className="space-y-4">
            <div className="space-y-2"><Label>Nome Completo *</Label><Input {...lancForm.register("nome")} placeholder="Nome completo" data-testid="input-lanc-nome" />{lancForm.formState.errors.nome && <p className="text-xs text-destructive">{lancForm.formState.errors.nome.message}</p>}</div>
            <div className="space-y-2"><Label>Endereco</Label><Input {...lancForm.register("endereco")} placeholder="Endereco completo" data-testid="input-lanc-endereco" /></div>
            <div className="space-y-2"><Label>Telefone</Label><Input {...lancForm.register("telefone")} placeholder="(00) 00000-0000" data-testid="input-lanc-telefone" /></div>
            <div className="space-y-2"><Label>Quantidade de Dispositivos</Label><Input type="number" min="1" {...lancForm.register("quantidade_dispositivos")} data-testid="input-lanc-dispositivos" /></div>
            <div className="space-y-2"><Label>Valor (R$)</Label><Input type="number" step="0.01" min="0" {...lancForm.register("valor")} data-testid="input-lanc-valor" /></div>
            <div className="space-y-2"><Label>Codigo Launcher BOX</Label><Input {...lancForm.register("codigo_launcher")} placeholder="Codigo do Launcher" data-testid="input-lanc-launcher" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Codigos Launcher adicionais</Label>
                <Button
                  type="button" variant="ghost" size="sm"
                  className="h-7 px-2 text-primary hover:text-primary"
                  onClick={() => setCodigosLauncher(arr => [...arr, ""])}
                  data-testid="add-codigo-launcher-rev-aco-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                </Button>
              </div>
              {codigosLauncher.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Clique em "Adicionar" para cadastrar mais codigos.</p>
              )}
              {codigosLauncher.map((c, idx) => (
                <div key={`cl-${idx}`} className="flex items-center gap-2" data-testid={`codigo-launcher-rev-aco-${idx}`}>
                  <Input
                    placeholder="Codigo do Launcher"
                    value={c}
                    onChange={e => setCodigosLauncher(arr => {
                      const a = [...arr]; a[idx] = e.target.value; return a;
                    })}
                  />
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-9 w-9 shrink-0 text-red-500 hover:text-red-600"
                    onClick={() => setCodigosLauncher(arr => arr.filter((_, i) => i !== idx))}
                    data-testid={`remove-codigo-launcher-rev-aco-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="space-y-2"><Label>Observacao</Label><Textarea {...lancForm.register("observacao")} placeholder="Observacoes..." data-testid="input-lanc-obs" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLancDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-lanc-btn">{submitting ? "Salvando..." : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar Exclusao Total */}
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Excluir Todos os Clientes</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Tem certeza que deseja excluir todos os clientes de <strong>{deleteAllTarget?.nome}</strong>? Esta acao nao pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleConfirmDeleteAll}>Excluir Todos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Funcionarios */}
      <Dialog open={dConfigFunc} onOpenChange={setDConfigFunc}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Gerenciar Funcionarios</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => { e.preventDefault(); if (!novoFunc.trim()) return; try { await authAxios.post("/hub-empresa/funcionarios", { nome: novoFunc.trim() }); toast.success("Funcionario criado!"); setNovoFunc(""); fetchFuncionarios(); } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); } }} className="flex gap-2">
              <Input value={novoFunc} onChange={(e) => setNovoFunc(e.target.value)} placeholder="Nome do funcionario..." className="flex-1" />
              <Button type="submit" size="sm"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {funcionarios.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum funcionario cadastrado</p>
              ) : funcionarios.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{f.nome}</p>
                    <p className="text-xs text-muted-foreground truncate">{`${window.location.origin}/hub-empresa-func/${f.token}`}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => { const url = `${window.location.origin}/hub-empresa-func/${f.token}`; try { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); } catch { toast.info(url); } }}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => { if (!window.confirm(`Excluir ${f.nome}?`)) return; try { await authAxios.delete(`/hub-empresa/funcionarios/${f.id}`); toast.success("Removido!"); fetchFuncionarios(); } catch { toast.error("Erro"); } }}><X className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
